import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';

import { api } from '@starling/core/api/client';
import type { components } from '@/api/types.gen';

// Ленты объявлений Vision — публичное чтение (без auth).
// Контракт: docs/api/openapi-vision.yaml (тег vision/public).

export type Listing = components['schemas']['Listing'];
export type ListingPage = components['schemas']['ListingPage'];
export type ListingFeed = components['schemas']['ListingFeed'];
export type ListingCategory = components['schemas']['ListingCategory'];
export type ListingCategoriesResponse = components['schemas']['ListingCategoriesResponse'];

export interface ListingsQueryParams {
  /** Лента обязательна (VD4 контракта). */
  feed: ListingFeed;
  /** Точное совпадение города. UI-контрола в V1.2-b нет — параметр на вырост. */
  city?: string;
  /** Slug категории из GET /categories. */
  category?: string;
  /** Текстовый поиск по title/description. */
  q?: string;
  limit?: number;
}

export function buildListingsUrl(params: ListingsQueryParams, cursor?: string): string {
  const search = new URLSearchParams({ feed: params.feed });
  if (params.city) search.set('city', params.city);
  if (params.category) search.set('category', params.category);
  if (params.q) search.set('q', params.q);
  if (params.limit) search.set('limit', String(params.limit));
  if (cursor) search.set('cursor', cursor);
  return `/api/v1/vision/listings?${search.toString()}`;
}

/**
 * Лента с keyset-пагинацией (VD3): страницы докручиваются кнопкой «Показать ещё»
 * — `next_cursor` предыдущей страницы уходит в `cursor` следующей; null = конец.
 */
export function useListingsQuery(params: ListingsQueryParams) {
  return useInfiniteQuery({
    queryKey: ['vision', 'listings', params],
    queryFn: ({ pageParam }) => api.get<ListingPage>(buildListingsUrl(params, pageParam)),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: ListingPage) => lastPage.next_cursor ?? undefined,
  });
}

/** Справочник категорий: меняется миграциями, поэтому кэш длинный. */
export function useCategoriesQuery() {
  return useQuery({
    queryKey: ['vision', 'categories'],
    queryFn: () => api.get<ListingCategoriesResponse>('/api/v1/vision/categories'),
    staleTime: 60 * 60 * 1000,
  });
}

export function useListingQuery(listingId: string | undefined) {
  return useQuery({
    enabled: Boolean(listingId),
    queryKey: ['vision', 'listings', 'detail', listingId],
    queryFn: () => api.get<Listing>(`/api/v1/vision/listings/${listingId}`),
  });
}

/** Название категории на активном языке; неизвестный slug → сам slug. */
export function categoryLabel(
  categories: ListingCategory[] | undefined,
  categoryId: string,
  language: string,
): string {
  const found = categories?.find((c) => c.id === categoryId);
  if (!found) return categoryId;
  return language.startsWith('kk') ? found.name_kk : found.name_ru;
}

export type ListingReportCreate = components['schemas']['ListingReportCreate'];
export type StatusOk = components['schemas']['StatusOk'];

/**
 * Жалоба на объявление (`POST /vision/listings/{id}/report`) — публичная,
 * bearer опционален (битый/протухший токен = аноним, VD7): клиент шлёт токен,
 * если сессия есть, но её отсутствие форму не ломает. Для существующего
 * active-объявления ответ всегда `{"status":"ok"}` — rate-limit и дедуп
 * наружу не видны, «спасибо» показываем безусловно.
 */
export function useReportListingMutation(listingId: string) {
  return useMutation({
    mutationKey: ['vision', 'listings', 'report', listingId],
    mutationFn: (input: ListingReportCreate) =>
      api.post<StatusOk>(`/api/v1/vision/listings/${listingId}/report`, input),
  });
}
